import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const CityFormModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  city = null, 
  isLoading = false 
}) => {
  const [activeTab, setActiveTab] = useState('ar');
  const [formData, setFormData] = useState({
    nameAr: '',
    nameEn: '',
    nameTr: '',
    svgX: 0,
    svgY: 0,
    colorZone: 1,
    clickZone: '',
    isActive: true
  });
  const [errors, setErrors] = useState({});

  const tabs = [
    { id: 'ar', label: 'العربية', icon: 'Globe' },
    { id: 'en', label: 'English', icon: 'Globe' },
    { id: 'tr', label: 'Türkçe', icon: 'Globe' }
  ];

  useEffect(() => {
    if (city) {
      setFormData({
        nameAr: city?.nameAr || '',
        nameEn: city?.nameEn || '',
        nameTr: city?.nameTr || '',
        svgX: city?.svgX || 0,
        svgY: city?.svgY || 0,
        colorZone: typeof city?.colorZone === 'number' ? city?.colorZone : (parseInt(city?.colorZone) || 1),
        clickZone: city?.clickZone || '',
        isActive: city?.isActive !== undefined ? city?.isActive : true
      });
    } else {
      setFormData({
        nameAr: '',
        nameEn: '',
        nameTr: '',
        svgX: 0,
        svgY: 0,
        colorZone: 1,
        clickZone: '',
        isActive: true
      });
    }
    setErrors({});
    setActiveTab('ar');
  }, [city, isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.nameAr?.trim()) {
      newErrors.nameAr = 'الاسم باللغة العربية مطلوب';
    }
    
    if (formData?.svgX < 0 || formData?.svgX > 1000) {
      newErrors.svgX = 'إحداثي X يجب أن يكون بين 0 و 1000';
    }
    
    if (formData?.svgY < 0 || formData?.svgY > 1000) {
      newErrors.svgY = 'إحداثي Y يجب أن يكون بين 0 و 1000';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };

  const handleMapClick = (e) => {
    const rect = e?.currentTarget?.getBoundingClientRect();
    const x = ((e?.clientX - rect?.left) / rect?.width) * 1000;
    const y = ((e?.clientY - rect?.top) / rect?.height) * 1000;
    
    setFormData(prev => ({
      ...prev,
      svgX: Math.round(x),
      svgY: Math.round(y)
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-300 bg-background/80 backdrop-blur-sm animate-fade-in">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="w-full max-w-4xl bg-card border border-border rounded-lg shadow-elevation-3 animate-slide-up">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="MapPin" size={20} color="white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">
                  {city ? 'تحرير المدينة' : 'إضافة مدينة جديدة'}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {city ? 'تحديث بيانات المدينة وإحداثياتها' : 'إنشاء مدينة جديدة مع تحديد موقعها'}
                </p>
              </div>
            </div>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              iconName="X"
              iconSize={20}
              disabled={isLoading}
            />
          </div>

          <form onSubmit={handleSubmit}>
            <div className="flex flex-col lg:flex-row">
              {/* Left Panel - Form */}
              <div className="flex-1 p-6 space-y-6">
                {/* Language Tabs */}
                <div className="border-b border-border">
                  <nav className="flex space-x-1">
                    {tabs?.map((tab) => (
                      <button
                        key={tab?.id}
                        type="button"
                        className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                          activeTab === tab?.id
                            ? 'bg-primary text-primary-foreground border-b-2 border-primary'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                        }`}
                        onClick={() => setActiveTab(tab?.id)}
                      >
                        <Icon name={tab?.icon} size={16} />
                        <span>{tab?.label}</span>
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Name Fields */}
                <div className="space-y-4">
                  {activeTab === 'ar' && (
                    <Input
                      label="اسم المدينة بالعربية *"
                      type="text"
                      value={formData?.nameAr}
                      onChange={(e) => handleInputChange('nameAr', e?.target?.value)}
                      error={errors?.nameAr}
                      placeholder="أدخل اسم المدينة بالعربية"
                      required
                      className="text-right"
                      dir="rtl"
                    />
                  )}
                  
                  {activeTab === 'en' && (
                    <Input
                      label="City Name in English"
                      type="text"
                      value={formData?.nameEn}
                      onChange={(e) => handleInputChange('nameEn', e?.target?.value)}
                      placeholder="Enter city name in English"
                    />
                  )}
                  
                  {activeTab === 'tr' && (
                    <Input
                      label="Şehir Adı Türkçe"
                      type="text"
                      value={formData?.nameTr}
                      onChange={(e) => handleInputChange('nameTr', e?.target?.value)}
                      placeholder="Türkçe şehir adını girin"
                    />
                  )}
                </div>

                {/* Coordinates */}
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="إحداثي X"
                    type="number"
                    value={formData?.svgX}
                    onChange={(e) => handleInputChange('svgX', parseInt(e?.target?.value) || 0)}
                    error={errors?.svgX}
                    min="0"
                    max="1000"
                    placeholder="0-1000"
                  />
                  
                  <Input
                    label="إحداثي Y"
                    type="number"
                    value={formData?.svgY}
                    onChange={(e) => handleInputChange('svgY', parseInt(e?.target?.value) || 0)}
                    error={errors?.svgY}
                    min="0"
                    max="1000"
                    placeholder="0-1000"
                  />
                </div>

                {/* Color Zone */}
                <div className="space-y-3">
                  <Input
                    label="منطقة اللون"
                    type="number"
                    value={formData?.colorZone}
                    onChange={(e) => handleInputChange('colorZone', parseInt(e?.target?.value) || 0)}
                    min="0"
                    placeholder="أدخل رقم منطقة اللون"
                    description="رقم صحيح يمثل منطقة اللون"
                  />
                </div>

                {/* Status Toggle */}
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData?.isActive}
                    onChange={(e) => handleInputChange('isActive', e?.target?.checked)}
                    className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2"
                  />
                  <label htmlFor="isActive" className="text-sm font-medium text-foreground">
                    المدينة نشطة
                  </label>
                </div>
              </div>

              {/* Right Panel - Map Preview */}
              <div className="w-full lg:w-96 p-6 border-t lg:border-t-0 lg:border-l border-border bg-muted/30">
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Icon name="Map" size={18} className="text-muted-foreground" />
                    <h3 className="text-sm font-medium text-foreground">معاينة الخريطة</h3>
                  </div>
                  
                  <div 
                    className="relative w-full h-64 bg-background border border-border rounded-lg cursor-crosshair overflow-hidden"
                    onClick={handleMapClick}
                  >
                    {/* Simple Map Background */}
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-green-50">
                      <div className="absolute inset-4 border-2 border-dashed border-muted-foreground/20 rounded" />
                    </div>
                    
                    {/* City Marker */}
                    <div
                      className="absolute w-4 h-4 transform -translate-x-2 -translate-y-2 transition-all duration-200"
                      style={{
                        left: `${(formData?.svgX / 1000) * 100}%`,
                        top: `${(formData?.svgY / 1000) * 100}%`,
                        backgroundColor: '#2563eb'
                      }}
                    >
                      <div className="w-full h-full rounded-full border-2 border-white shadow-lg" />
                      <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-t-4 border-transparent border-t-white" />
                    </div>
                    
                    <div className="absolute bottom-2 left-2 text-xs text-muted-foreground bg-background/80 px-2 py-1 rounded">
                      انقر لتحديد الموقع
                    </div>
                  </div>
                  
                  <div className="text-xs text-muted-foreground space-y-1">
                    <div>الإحداثيات: ({formData?.svgX}, {formData?.svgY})</div>
                    <div>منطقة اللون: {formData?.colorZone}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-border bg-muted/30">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isLoading}
              >
                إلغاء
              </Button>
              
              <Button
                type="submit"
                variant="default"
                loading={isLoading}
                iconName="Save"
                iconPosition="left"
              >
                {city ? 'تحديث المدينة' : 'إضافة المدينة'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CityFormModal;